import { useEventStore } from "@/event";
import { EVENT_CONVERSION_OFFERS_CONFIG as CHRISTMAS_EVENT_CONVERSION_OFFERS_CONFIG } from "@/game/config/events/christmas/conversionOffers";
import { EVENT_CONVERSION_OFFERS_CONFIG as EASTER_EVENT_CONVERSION_OFFERS_CONFIG } from "@/game/config/events/easter/conversionOffers";
import { EVENT_CONVERSION_OFFERS_CONFIG as FIREFEST_EVENT_CONVERSION_OFFERS_CONFIG } from "@/game/config/events/firefest/conversionOffers";
import { EVENT_CONVERSION_OFFERS_CONFIG as FISHING_EVENT_CONVERSION_OFFERS_CONFIG } from "@/game/config/events/fishing/conversionOffers";
import { EVENT_CONVERSION_OFFERS_CONFIG as HALLOWEEN_EVENT_CONVERSION_OFFERS_CONFIG } from "@/game/config/events/halloween/conversionOffers";
import { EVENT_CONVERSION_OFFERS_CONFIG as HUNTING_EVENT_CONVERSION_OFFERS_CONFIG } from "@/game/config/events/hunting/conversionOffers";
import { EVENT_CONVERSION_OFFERS_CONFIG as PIRATES_EVENT_CONVERSION_OFFERS_CONFIG } from "@/game/config/events/pirates/conversionOffers";
import { EVENT_CONVERSION_OFFERS_CONFIG as SOLARFAIR_EVENT_CONVERSION_OFFERS_CONFIG } from "@/game/config/events/solarfair/conversionOffers";
import type { Offer } from "@/game/config/gameCurrencyOffers";
import { GAME_CURRENCY_OFFERS_CONFIG } from "@/game/config/gameCurrencyOffers";
import { ResourceType } from "@/game/entities/config/resources";
import { EventUpgradeIds } from "@/game/entities/config/upgrades";
import { getUnlocksByLevel } from "@/game/utils/resources";
import { getUpgradeCurrentReplaceValueById } from "@/game/utils/upgrades";
import useQuestStore from "@/store/useQuestStore";
import useResourcesStorageStore from "@/store/useResourcesStorageStore";
import useResourcesStore from "@/store/useResourcesStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useShopStore from "@/store/useShopStore";
import useUserStore from "@/store/useUserStore";
import type { QuestActive } from "@/types/quest";
import type { ConversionOffer, ShopItem } from "@/types/shop";
import { EventCategory, OfferType } from "@/types/ui";
import { ceilNumber, floorNumber, getNumber } from "@/utils/number";
import { filterBySkinPrefix } from "@/utils/shop/skins";
import { ref, toRaw, type Ref } from "vue";

export enum CalculationType {
	REGULAR = "REGULAR",
	PIGGY_BANK = "PIGGY_BANK",
	RESOURCES_FOR_GEMS_OFFERS = "RESOURCES_FOR_GEMS_OFFERS",
	EVENTIUM_OFFERS = "EVENTIUM_OFFERS",
	FULL = "FULL",
}

export type ExtendedPiggyBankOffer = ShopItem & {
	activeQuest: QuestActive | undefined;
	questPercentageProgress: number;
	questCompleted: boolean;
};

export type CoinCalcFn = (income: number) => number;
export type ResourceCalcFn = (storage: number, income: number) => number;

// Defined outside to avoid re-allocation
const COOLDOWN_MS = 3 * 60 * 60 * 1000;

// Pre-flattened config to avoid spread operations inside computed
const CURRENCY_PACKS_SOURCE = [
	...GAME_CURRENCY_OFFERS_CONFIG.COINS_FOR_GEMS_OFFERS,
	...GAME_CURRENCY_OFFERS_CONFIG.RESOURCES_FOR_GEMS_OFFERS,
].map((p: [number, Offer]): Offer => p[1]);

class OfferProviderClass {
	// Lazy store accessors
	// NOTE: These replace constructor assignments to fix the Pinia Lifecycle error
	// The store is retrieved only when first accessed
	private _shopStore?: ReturnType<typeof useShopStore>;
	private get shopStore(): ReturnType<typeof useShopStore> {
		return (this._shopStore ??= useShopStore());
	}

	private _settlementsStore?: ReturnType<typeof useSettlementsStore>;
	private get settlementsStore(): ReturnType<typeof useSettlementsStore> {
		return (this._settlementsStore ??= useSettlementsStore());
	}

	private _questStore?: ReturnType<typeof useQuestStore>;
	private get questStore(): ReturnType<typeof useQuestStore> {
		return (this._questStore ??= useQuestStore());
	}

	private _userStore?: ReturnType<typeof useUserStore>;
	private get userStore(): ReturnType<typeof useUserStore> {
		return (this._userStore ??= useUserStore());
	}

	private _resourcesStore?: ReturnType<typeof useResourcesStore>;
	private get resourcesStore(): ReturnType<typeof useResourcesStore> {
		return (this._resourcesStore ??= useResourcesStore());
	}

	private _resourcesStorageStore?: ReturnType<
		typeof useResourcesStorageStore
	>;

	private get resourcesStorageStore(): ReturnType<
		typeof useResourcesStorageStore
	> {
		return (this._resourcesStorageStore ??= useResourcesStorageStore());
	}

	private _eventStore?: ReturnType<typeof useEventStore>;
	private get eventStore(): ReturnType<typeof useEventStore> {
		return (this._eventStore ??= useEventStore());
	}

	// Reactive state
	public extendedPiggyBankOffers = ref<ExtendedPiggyBankOffer[] | null>(null);
	public resourcesForGemsOffers = ref<Offer[] | null>(null);
	public eventiumOffers = ref<ConversionOffer[] | null>(null);
	public eventiumFreePack = ref<ConversionOffer | null>(null);
	public regularFreePack = ref<ConversionOffer | null>(null);

	public get resourcesUnlocks(): Partial<Record<ResourceType, boolean>> {
		const currentLevel = this.settlementsStore.mainLevel;
		const result: Partial<Record<ResourceType, boolean>> = {};
		const unlockConfig = getUnlocksByLevel();
		const UNLOCKABLE_RESOURCE_TYPES = Object.keys(
			unlockConfig,
		) as ResourceType[];
		const len = UNLOCKABLE_RESOURCE_TYPES.length;

		for (let i = 0; i < len; i++) {
			const key = UNLOCKABLE_RESOURCE_TYPES[i];

			// Access level directly from the cached object
			const requiredLevel = unlockConfig[key];

			// DEFENSIVE CHECK
			// If requiredLevel is undefined, the resource is considered "never unlocked"
			if (requiredLevel === undefined) {
				result[key] = false;
				continue;
			}

			// Direct boolean assignment (fastest path)
			result[key] = requiredLevel <= currentLevel;
		}

		return result;
	}

	public lastUpdated: Ref<Date | null> = ref(null);

	public constructor() {
		// Constructor is empty, Pinia stores are handled by getters
	}

	// Triggers specific extension methods for each offer type.
	public calculate(
		calculationTypes:
			| CalculationType[]
			| CalculationType = CalculationType.FULL,
	): void {
		// Normalize input to an array
		const types = Array.isArray(calculationTypes)
			? calculationTypes
			: [calculationTypes];
		const isFull = types.includes(CalculationType.FULL);

		// Define calculation tasks mapping
		const tasks: Partial<Record<CalculationType, () => void>> = {
			[CalculationType.PIGGY_BANK]: (): void => {
				this.extendedPiggyBankOffers.value =
					this.calculateExtendPiggyBankOffers();
			},
			[CalculationType.RESOURCES_FOR_GEMS_OFFERS]: (): void => {
				this.resourcesForGemsOffers.value =
					this.calculateResourcesForGemsOffers();
			},
			[CalculationType.EVENTIUM_OFFERS]: (): void => {
				this.eventiumOffers.value = this.calculateEventiumOffers();
				this.eventiumFreePack.value = this.calculateEventiumFreePack();
			},
			[CalculationType.REGULAR]: (): void => {
				this.regularFreePack.value = this.calculateRegularFreePack();
			},
		};

		// Execute tasks based on types or FULL flag
		(Object.entries(tasks) as [CalculationType, () => void][]).forEach(
			([type, execute]: [CalculationType, () => void]): void => {
				if (isFull || types.includes(type)) {
					execute();
				}
			},
		);

		this.lastUpdated.value = new Date();
	}

	private calculateExtendPiggyBankOffers(): ExtendedPiggyBankOffer[] | null {
		const activeQuests = toRaw(this.questStore.getActiveQuests);
		const mainLevel = this.settlementsStore.mainLevel;
		const rawOffers = toRaw(this.shopStore.piggyBankOffers);

		if (!rawOffers) return null;

		return rawOffers.map((offer: ShopItem): ExtendedPiggyBankOffer => {
			// Find the quest associated with the offer's activity attribute
			const activeQuest = activeQuests.find(
				(q: QuestActive): boolean =>
					"activity" in offer.attributes &&
					q.id === offer.attributes.activity,
			);

			const progress = this.calculateQuestProgress(activeQuest); // percentage value
			const isQuestCompleted = progress >= 100;

			// Extract and override the visibility logic for the left box.
			// Hide the offer in the left box if the quest is completed
			// and the player has reached or exceeded level 25.
			const showInLeftBox =
				offer.show_in_left_box &&
				!(isQuestCompleted && mainLevel >= 25);

			return {
				...offer,
				show_in_left_box: showInLeftBox, // Overriding the original attribute
				activeQuest,
				questPercentageProgress: progress,
				questCompleted: isQuestCompleted,
			};
		});
	}

	private calculateQuestProgress(quest: QuestActive | undefined): number {
		if (!quest?.objective?.amount) return 0;

		const ratio = +quest.progress / +quest.objective.amount;

		if (ratio >= 1) return 100;

		return floorNumber(ratio * 100);
	}

	private calculateResourcesForGemsOffers = (): Offer[] => {
		const stamps = toRaw(this.userStore.marketplaceTimestamps);
		const unlocks = this.resourcesUnlocks;
		const now = Date.now();

		const buffer: Offer[] = [];
		const packCount = CURRENCY_PACKS_SOURCE.length;
		const coinsIncome = this.resourcesStore.coinsUncappedIncome ?? 0;
		const storeData = this.resourcesStore as unknown as Record<
			string,
			number
		>;
		const storageStoreData = this
			.resourcesStorageStore as unknown as Record<string, number>;

		for (let i = 0; i < packCount; i++) {
			const pack = CURRENCY_PACKS_SOURCE[i];
			const packValue = pack.value;
			if (!packValue) continue;

			const type = pack.type;
			let calculatedValue: Record<string, number> | null = null;

			if (type === OfferType.COINS_FOR_GEMS) {
				const rawCoinValue = packValue[ResourceType.COINS];
				const val =
					typeof rawCoinValue === "function"
						? (rawCoinValue as CoinCalcFn)(coinsIncome)
						: (rawCoinValue as number);

				if (val > 0) calculatedValue = { [ResourceType.COINS]: val };
			} else {
				const resKey = Object.keys(packValue)[0] as ResourceType;

				if (resKey && unlocks[resKey]) {
					const storage = storageStoreData[resKey];
					const income = storeData[`${resKey}UncappedIncome`];

					if (storage !== undefined && income !== undefined) {
						const rawVal = packValue[resKey];
						const val =
							typeof rawVal === "function"
								? (rawVal as ResourceCalcFn)(storage, income)
								: (rawVal as number);

						if (val > 0) calculatedValue = { [resKey]: val };
					}
				}
			}

			if (!calculatedValue) continue;

			// Cooldown logic
			const startTime = stamps[`${type}_${pack.id}`];
			let timeLimit: number | null = null;
			let ribbonInfo: string | null = null;

			if (startTime) {
				const diff = startTime + COOLDOWN_MS - now;
				if (diff > 0) {
					timeLimit = (diff / 1000) | 0;
					ribbonInfo = "ui.purchase_frequency";
				}
			}

			buffer.push({
				...pack,
				value: calculatedValue,
				timeLimit,
				ribbonInfo,
			});
		}

		return buffer;
	};

	public get freeRegularOfferAvailable(): boolean {
		const isClaimed = this.userStore.isDailyFreeRegularPackClaimed;

		// If never claimed, the offer is available
		if (!isClaimed) return true;

		// Offer is available only if the last claim date is different from today
		return new Date(isClaimed).toDateString() !== new Date().toDateString();
	}

	public get freeEventOfferAvailable(): boolean {
		if (!this.eventStore.isActive) return false;

		const isClaimed = this.userStore.isDailyFreeEventPackClaimed;
		const hasOffer = this.eventStore.attributes?.has_super_clicker_offer;

		if (!isClaimed) return !!hasOffer;

		const isAnotherDay =
			new Date(isClaimed).toDateString() !== new Date().toDateString();

		return !!isAnotherDay && !!hasOffer;
	}

	private get offersConversionBonus(): number {
		// Include purchased event offers even after they reach their purchase limit.
		const offers = filterBySkinPrefix(
			toRaw(this.shopStore.items),
			"event_",
		);

		if (!offers || offers.length === 0) return 0;

		let totalPercentage = 0;
		const len = offers.length;

		for (let i = 0; i < len; i++) {
			const entry = offers[i];
			const purchases = entry.num_of_purchases || 0;

			// Skip unpurchased or invalid records
			if (purchases === 0 || !entry.attributes?.slots) continue;

			const slots = entry.attributes.slots;
			const slotsLen = slots.length;

			for (let j = 0; j < slotsLen; j++) {
				const items = slots[j].items;
				if (!items) continue;

				const itemsLen = items.length;

				for (let k = 0; k < itemsLen; k++) {
					const item = items[k];

					if (
						"key" in item &&
						typeof item.key === "string" &&
						item.key.includes("event_conversion_offers_rate")
					) {
						// In-memory calculation (no array allocation)
						totalPercentage += getNumber(item.key) * purchases;
					}
				}
			}
		}

		return totalPercentage / 100;
	}

	// Final bonus total (Base + Upgrades + Offers)
	public get conversionBonus(): number {
		let upgradesConversionBonus = 0;
		const eventType = this.eventStore.attributes?.type;

		switch (eventType) {
			case EventCategory.CHRISTMAS:
				upgradesConversionBonus = getUpgradeCurrentReplaceValueById(
					EventUpgradeIds.ch_trading,
				);
				break;
			case EventCategory.EASTER:
				upgradesConversionBonus = getUpgradeCurrentReplaceValueById(
					EventUpgradeIds.ea_trading,
				);
				break;
			case EventCategory.FISHING:
				upgradesConversionBonus = getUpgradeCurrentReplaceValueById(
					EventUpgradeIds.fi_trading,
				);
				break;
			case EventCategory.FIREFEST:
				upgradesConversionBonus = getUpgradeCurrentReplaceValueById(
					EventUpgradeIds.ff_trading,
				);
				break;
			case EventCategory.HUNTING:
				upgradesConversionBonus = getUpgradeCurrentReplaceValueById(
					EventUpgradeIds.hu_trading,
				);
				break;
			case EventCategory.HALLOWEEN:
				upgradesConversionBonus = getUpgradeCurrentReplaceValueById(
					EventUpgradeIds.ha_trading,
				);
				break;
		}

		return 1 + upgradesConversionBonus + this.offersConversionBonus;
	}

	private getEventConversionConfig():
		| typeof CHRISTMAS_EVENT_CONVERSION_OFFERS_CONFIG
		| typeof EASTER_EVENT_CONVERSION_OFFERS_CONFIG
		| typeof FISHING_EVENT_CONVERSION_OFFERS_CONFIG
		| typeof FIREFEST_EVENT_CONVERSION_OFFERS_CONFIG
		| typeof SOLARFAIR_EVENT_CONVERSION_OFFERS_CONFIG
		| typeof HUNTING_EVENT_CONVERSION_OFFERS_CONFIG
		| typeof HALLOWEEN_EVENT_CONVERSION_OFFERS_CONFIG
		| typeof PIRATES_EVENT_CONVERSION_OFFERS_CONFIG
		| null {
		const eventType = this.eventStore.attributes?.type;

		switch (eventType) {
			case EventCategory.CHRISTMAS:
				return CHRISTMAS_EVENT_CONVERSION_OFFERS_CONFIG;
			case EventCategory.EASTER:
				return EASTER_EVENT_CONVERSION_OFFERS_CONFIG;
			case EventCategory.FISHING:
				return FISHING_EVENT_CONVERSION_OFFERS_CONFIG;
			case EventCategory.FIREFEST:
				return FIREFEST_EVENT_CONVERSION_OFFERS_CONFIG;
			case EventCategory.SOLAR:
				return SOLARFAIR_EVENT_CONVERSION_OFFERS_CONFIG;
			case EventCategory.HUNTING:
				return HUNTING_EVENT_CONVERSION_OFFERS_CONFIG;
			case EventCategory.HALLOWEEN:
				return HALLOWEEN_EVENT_CONVERSION_OFFERS_CONFIG;
			case EventCategory.PIRATES:
				return PIRATES_EVENT_CONVERSION_OFFERS_CONFIG;
			default:
				return null;
		}
	}

	private calculateEventiumOffers = (): ConversionOffer[] => {
		const stamps = toRaw(this.userStore.marketplaceTimestamps);
		const unlocks = this.resourcesUnlocks;
		const now = Date.now();

		const config = this.getEventConversionConfig();
		const bonus = this.conversionBonus;

		const buffer: ConversionOffer[] = [];
		const coinsIncome = this.resourcesStore.coinsUncappedIncome ?? 0;
		const storeData = this.resourcesStore as unknown as Record<
			string,
			number
		>;

		const sourcePacks = [
			...(config?.COINS_FOR_EVENTIUM || []),
			...(config?.RESOURCES_FOR_EVENTIUM_OFFERS || []),
		];
		const packCount = sourcePacks.length;

		for (let i = 0; i < packCount; i++) {
			const pack = sourcePacks[i][1];
			if (!pack || !pack.value) continue;

			const type = pack.type;
			let calculatedValue: Record<string, number> | null = null;

			if (type === OfferType.COINS_FOR_EVENTIUM) {
				const rawCoinValue = pack.value[ResourceType.COINS];
				let val =
					typeof rawCoinValue === "function"
						? (rawCoinValue as CoinCalcFn)(coinsIncome)
						: (rawCoinValue as number);

				if (val > 0) {
					val = ceilNumber(val * bonus);
					calculatedValue = { [ResourceType.COINS]: val };
				}
			} else if (type === OfferType.RESOURCES_FOR_EVENTIUM) {
				const resKey = Object.keys(pack.value)[0] as ResourceType;

				if (resKey && unlocks[resKey]) {
					const storage = storeData[resKey];
					const income = storeData[`${resKey}UncappedIncome`];

					if (storage !== undefined && income !== undefined) {
						const rawVal = pack.value[resKey];
						let val =
							typeof rawVal === "function"
								? (rawVal as ResourceCalcFn)(storage, income)
								: (rawVal as number);

						if (val > 0) {
							val = ceilNumber(val * bonus);
							calculatedValue = { [resKey]: val };
						}
					}
				}
			}

			if (!calculatedValue) continue;

			// Cooldown logic
			const startTime = stamps[`${type}_${pack.id}`];
			let timeLimit: number | null = null;
			let ribbonInfo: string | null = null;

			if (startTime) {
				const diffMs = startTime + COOLDOWN_MS - now;
				if (diffMs > 0) {
					timeLimit = ceilNumber(diffMs / 1000);
					ribbonInfo = "ui.purchase_frequency";
				}
			}

			buffer.push({
				...pack,
				value: calculatedValue,
				timeLimit,
				ribbonInfo,
			});
		}

		return buffer;
	};

	private calculateEventiumFreePack = (): ConversionOffer | null => {
		if (!this.eventStore.attributes?.has_super_clicker_offer) return null;

		const config = this.getEventConversionConfig();
		const packs = config?.SUPER_CLICKER_FREE;

		if (!packs) return null;

		// Find the item directly from the array/map structure
		const foundPack = Array.from(packs.values()).find(
			(item: ConversionOffer): boolean =>
				item.type === "super_clicker_free",
		);

		if (!foundPack) return null;

		// Create a copy and update limit if the offer is not available
		const resultPack = { ...foundPack };

		if (!this.freeEventOfferAvailable && resultPack.limit) {
			resultPack.limit = { ...resultPack.limit, current: 1 };
		}

		return resultPack;
	};

	private calculateRegularFreePack = (): ConversionOffer | null => {
		const config = GAME_CURRENCY_OFFERS_CONFIG.SUPER_CLICKER_FREE;
		if (!config) return null;

		// Convert Map to array and find the item directly
		const foundPack = Array.from(config.values()).find(
			(item: ConversionOffer): boolean =>
				item.type === "super_clicker_free",
		);

		if (!foundPack) return null;

		// Create a copy and update limit if the offer is not available
		const resultPack = { ...foundPack };

		if (!this.freeRegularOfferAvailable && resultPack.limit) {
			resultPack.limit = { ...resultPack.limit, current: 1 };
		}

		return resultPack;
	};
}

export const OfferProvider = new OfferProviderClass();
